<script setup lang="ts">
import { ref, onMounted } from 'vue'
import * as api from '../../api/tecdocApi'
import type { ArticleDebugResponse } from '../../api/tecdocApi'

const props = defineProps<{
  sku: string
}>()

const emit = defineEmits<{
  close: []
}>()

const loading = ref(true)
const error = ref<string | null>(null)
const data = ref<ArticleDebugResponse | null>(null)

onMounted(async () => {
  try {
    data.value = await api.getArticleDebug(props.sku)
  } catch (err) {
    error.value = (err as { message?: string }).message ?? 'Kunne ikke hente debug-data'
  } finally {
    loading.value = false
  }
})

function pretty(value: unknown): string {
  if (value == null) return '(null)'
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

async function copyJson(value: unknown) {
  try {
    await navigator.clipboard.writeText(pretty(value))
  } catch {
    // ignore clipboard failures — not fatal
  }
}

function onBackdrop(e: MouseEvent) {
  if (e.target === e.currentTarget) emit('close')
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('close')
}
</script>

<template>
  <Teleport to="body">
    <div class="debug-panel-backdrop" @click="onBackdrop" @keydown="onKeydown" tabindex="0">
      <div class="debug-panel" role="dialog" aria-modal="true">
        <header class="debug-panel__header">
          <h2>TecDoc debug — {{ sku }}</h2>
          <button class="debug-panel__close" @click="emit('close')" title="Lukk">&times;</button>
        </header>

        <div v-if="loading" class="debug-panel__loading">Laster rå TecDoc-data...</div>
        <div v-else-if="error" class="debug-panel__error">{{ error }}</div>
        <div v-else class="debug-panel__body">
          <section class="debug-panel__col">
            <div class="debug-panel__col-head">
              <h3>Nåværende henting</h3>
              <button class="debug-panel__copy" @click="copyJson(data?.current)">Kopier JSON</button>
            </div>
            <p class="debug-panel__hint">Det samme dataoppsettet som brukes av den vanlige produkt-flyten (GetAllInfo).</p>
            <div v-if="data?.currentError" class="debug-panel__error">{{ data.currentError }}</div>
            <pre class="debug-panel__json">{{ pretty(data?.current) }}</pre>
          </section>
          <section class="debug-panel__col">
            <div class="debug-panel__col-head">
              <h3>Med ekstra include-flagg</h3>
              <button class="debug-panel__copy" @click="copyJson(data?.withExtraFlags)">Kopier JSON</button>
            </div>
            <p class="debug-panel__hint">Samme kall, men med ekstra <code>include*</code>-flagg (linkages, compatibleVehicles, osv.). Hvis TecDoc returnerer nye felt her, er det ting vi kan eksponere i vanlig UI.</p>
            <div v-if="data?.withExtraFlagsError" class="debug-panel__error">{{ data.withExtraFlagsError }}</div>
            <pre class="debug-panel__json">{{ pretty(data?.withExtraFlags) }}</pre>
          </section>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.debug-panel-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.debug-panel {
  background: white;
  border-radius: 12px;
  width: min(1400px, 100%);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.8rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
    background: #1f2937;
    color: white;

    h2 {
      margin: 0;
      font-size: 1rem;
      font-family: monospace;
    }
  }

  &__close {
    background: transparent;
    color: white;
    border: none;
    font-size: 1.6rem;
    cursor: pointer;
    line-height: 1;
    padding: 0 6px;

    &:hover { color: #fbbf24; }
  }

  &__loading,
  &__error {
    padding: 2rem;
    text-align: center;
    color: #555;
  }

  &__error {
    color: #b91c1c;
    font-weight: 600;
  }

  &__body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    overflow: hidden;
    flex: 1;
    min-height: 0;
  }

  &__col {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-right: 1px solid #e5e7eb;
    padding: 1rem 1.25rem;

    &:last-child { border-right: none; }
  }

  &__col-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;

    h3 {
      margin: 0;
      font-size: 0.9rem;
      color: #1f2937;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  &__copy {
    background: #002868;
    color: white;
    border: none;
    padding: 3px 10px;
    font-size: 0.72rem;
    border-radius: 4px;
    cursor: pointer;

    &:hover { background: #001d4f; }
  }

  &__hint {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0 0 0.5rem 0;

    code {
      background: #f3f4f6;
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 0.9em;
    }
  }

  &__json {
    flex: 1;
    overflow: auto;
    background: #0f172a;
    color: #d1d5db;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: 0.72rem;
    font-family: 'Menlo', 'Consolas', monospace;
    line-height: 1.45;
    margin: 0;
    white-space: pre;
    min-height: 0;
  }
}

@media (max-width: 900px) {
  .debug-panel__body {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
  }
  .debug-panel__col {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    &:last-child { border-bottom: none; }
  }
}
</style>
